// src/components/UsedInvoiceNumbersList.jsx
// ============================================================================
// Collapsible disclosure list of invoice numbers already present in the DB.
// Filters to the YYYYMM prefix of `filterDate` (invoice numbers follow the
// YYYYMMSS format). Falls back to showing all invoices when no date is set.
// Sorted largest-first. Default-collapsed; first ~20 rows visible when open,
// scroll for the rest.
// ============================================================================
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const ROW_HEIGHT_PX = 20;
const VISIBLE_ROWS = 20;

export default function UsedInvoiceNumbersList({
  allInvoices,
  projectsById,
  filterDate,
  currentNumber,
}) {
  const [expanded, setExpanded] = useState(false);

  const items = useMemo(() => {
    const list = Array.isArray(allInvoices) ? allInvoices : [];
    const monthPrefix = (filterDate && /^\d{4}-\d{2}/.test(filterDate))
      ? filterDate.slice(0, 7).replace('-', '') // "2026-02" -> "202602"
      : null;

    return list
      .filter(inv => {
        const num = String(inv?.invoiceNumber || '').trim();
        if (!num) return false;
        if (!monthPrefix) return true;
        return num.startsWith(monthPrefix);
      })
      .map(inv => ({
        number: String(inv.invoiceNumber).trim(),
        projectId: inv.projectId,
        projectName: projectsById?.get?.(Number(inv.projectId))
          || projectsById?.get?.(String(inv.projectId))
          || '',
      }))
      .sort((a, b) => b.number.localeCompare(a.number, undefined, { numeric: true }));
  }, [allInvoices, projectsById, filterDate]);

  const monthLabel = (filterDate && /^\d{4}-\d{2}/.test(filterDate))
    ? filterDate.slice(0, 7)
    : null;

  return (
    <div style={{ marginTop: '6px' }}>
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="brio-flex brio-items-center brio-gap-xs"
        style={{
          background: 'none', border: 'none', padding: '2px 0',
          cursor: 'pointer', fontSize: '11px',
          color: 'var(--brio-grey-dark, #555)',
          fontFamily: 'var(--brio-font-family)',
        }}
      >
        {expanded
          ? <ChevronDown className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />
          : <ChevronRight className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />}
        <span>
          Show used invoice numbers ({items.length}
          {monthLabel ? ` in ${monthLabel}` : ' total'})
        </span>
      </button>
      {expanded && (
        <div
          style={{
            marginTop: '4px',
            border: '1px solid var(--brio-border)',
            background: 'var(--brio-grey-light, #f7f7f7)',
            maxHeight: `${ROW_HEIGHT_PX * VISIBLE_ROWS}px`,
            overflowY: 'auto',
            fontSize: '11px',
            fontFamily: 'var(--brio-font-family)',
          }}
        >
          {items.length === 0 ? (
            <div style={{ padding: '6px 8px', color: 'var(--brio-grey)' }}>
              No invoice numbers {monthLabel ? `for ${monthLabel}` : 'on record'} yet.
            </div>
          ) : (
            items.map((it, idx) => {
              const isCurrent = currentNumber && String(currentNumber).trim() === it.number;
              return (
                <div
                  key={`${it.number}-${idx}`}
                  style={{
                    height: `${ROW_HEIGHT_PX}px`,
                    lineHeight: `${ROW_HEIGHT_PX}px`,
                    padding: '0 8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    background: isCurrent ? 'var(--brio-gold-light, #fff8d6)' : undefined,
                    borderBottom: '1px solid var(--brio-border, #e5e5e5)',
                  }}
                >
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: isCurrent ? 600 : 400 }}>
                    {it.number}
                  </span>
                  <span
                    style={{
                      color: 'var(--brio-grey)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '60%',
                    }}
                    title={it.projectName || ''}
                  >
                    {it.projectName || '—'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
